import { Gym, useGym, useGyms } from "@api/gym";
import { usePersistedGym } from "@lib/auth";
import { useRouter } from "next/router";
import { useEffect } from "react";
import * as analytics from "@/lib/analytics";
import { useHeliosPagesFeature } from "@/lib/feature-flags/use-feature-flag";
import { useActiveOrg } from "./active-org";
import { sectionRootForPath } from "./section-root";

export function useActiveGym(): {
  gym: Gym | null;
  setGym: (gym: Gym | null) => void;
  loading: boolean;
} {
  const { org } = useActiveOrg();
  const [persistedGym, setPersistedGym] = usePersistedGym();
  const { enabled: pagesEnabled } = useHeliosPagesFeature();

  const router = useRouter();

  const { data: gyms } = useGyms(
    { org: org?.id, populate: "phoneNumber" },
    {
      continue: () => !!org?.id,
    },
  );

  useEffect(() => {
    if (!persistedGym && gyms && gyms.length > 0) {
      setPersistedGym(gyms[0]);
    }
  }, [persistedGym, gyms, setPersistedGym]);

  // Always have latest copy of org
  const { data: fetchedGym } = useGym(persistedGym?.id);

  // Persist latest copy of org
  useEffect(() => {
    if (!fetchedGym) {
      return;
    }
    setPersistedGym(fetchedGym);
  }, [fetchedGym, setPersistedGym]);

  const gym = persistedGym ?? gyms?.[0] ?? null;
  const gymId = gym?.id;
  const gymOrg = gym?.org;

  // Feature flags target gym and org ids, so PostHog learns the active gym as
  // soon as it resolves and again on every switch.
  useEffect(() => {
    if (!gymId || !gymOrg) {
      return;
    }
    analytics.setGymForFlags({ id: gymId, org: gymOrg });
  }, [gymId, gymOrg]);

  return {
    gym,
    setGym: (newGym) => {
      // Settings and integrations pages are scoped by the active gym directly,
      // so switching inboxes should stay on the current page and just swap the
      // data instead of bouncing back to the section root.
      const staysOnPage =
        router.pathname.startsWith("/settings") || router.pathname.startsWith("/integrations");
      if (
        newGym &&
        newGym.id !== persistedGym?.id &&
        !router.pathname.includes("go") &&
        !staysOnPage
      ) {
        router.replace(sectionRootForPath(router.pathname, pagesEnabled));
      }
      setPersistedGym(newGym);
    },
    loading: gyms === undefined,
  };
}

export default useActiveGym;
